import React from "react";
import { DiscussionWithoutContentDTO } from "@/generated-client";
import DateFormatter from "@/main/common/DateFormatter";
import { Localizer } from "@/main/react/Types";
import { getDisplayUsername, getUserProfilePath } from "@/main/common/UsernameDisplayUtils";
import { CardMetadataBadges } from "@/main/react/card/CardMetadataBadges";
import { getFriendlyCountBadgeLabel } from "@/main/react/card/CardMetadataBadgeCopy";
import SearchResultRowBase from "@/main/react/page/search/result-row/SearchResultRowBase";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import { getHideSensitiveMediaPreference } from "@/main/common/SensitivePreference";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import {type WithDiscussionAuthorAvatar} from "@/main/common/DiscussionAuthorAvatar";
import { onFeedCardActivated, type FeedCardAttribution } from "@/main/analytics/feedAttribution";
import { type SearchResultAttribution } from "@/main/analytics/searchResultAttribution";
import { buildHighlightTerms, highlightMatches } from "@/main/react/page/search/highlightMatches";

export function SearchDiscussionResultRow(props: {
    discussion: DiscussionWithoutContentDTO;
    /**
     * Set by the discussions vertical to make this row's activation measurable (`feed_type=search`).
     * Absent on every other vertical, which leaves those rows unchanged — a click on a group or a
     * user row is not a discussion view and has no `discussion_views` row to attribute.
     */
    feedAttribution?: FeedCardAttribution;
    /**
     * Threaded straight through to {@link SearchResultRowBase}, which owns the click beacon. This is
     * NOT a replacement for `feedAttribution` — the two answer different questions and both fire.
     * `discussion_views.source` covers anonymous traffic and joins to product data; this one carries
     * the query, which is the only way to ask whether a particular search worked.
     */
    searchAttribution?: SearchResultAttribution;
    localizer: Localizer;
}) {
    const { discussion, feedAttribution, searchAttribution, localizer } = props;
    // Derived from the attribution the row already receives; absent off the search page, where the
    // chooser dialogs reuse this row and there is no query to highlight against.
    const highlightTerms = React.useMemo(
        () => buildHighlightTerms(searchAttribution?.query),
        [searchAttribution?.query],
    );
    const discussionWithAuthorAvatar = discussion as WithDiscussionAuthorAvatar<DiscussionWithoutContentDTO>;
    const discussionWithFlags = discussion as DiscussionWithoutContentDTO & { sensitive?: boolean | null };
    const dateFormatter = new DateFormatter(localizer);
    const href = `/discussion/${encodeURIComponent(discussion.slug ?? discussion.publicId ?? "")}`;
    const author = getDisplayUsername(discussion.authorUsername, localizer);
    const authorProfilePath = getUserProfilePath(discussion.authorUsername, "discussions");
    const createdAt = discussion.createdAt ? dateFormatter.timeSince(discussion.createdAt) : "";
    const isSensitive = Boolean(discussionWithFlags.sensitive);
    const hideSensitiveMedia = getHideSensitiveMediaPreference();
    const warningCopy = React.useMemo(
        () => getMaskedContentWarningCopy(localizer, { isSensitive }),
        [isSensitive, localizer],
    );

    const upvotesLabel = getFriendlyCountBadgeLabel(localizer, "badge.thought.upvotes", discussion.agreementsCount);
    const downvotesLabel = getFriendlyCountBadgeLabel(localizer, "badge.thought.downvotes", discussion.disagreementsCount);
    const thoughtsLabel = getFriendlyCountBadgeLabel(localizer, "badge.user.thoughts", discussion.argumentsCount);

    const handleActivated = React.useCallback(() => {
        if (!feedAttribution) {
            return;
        }
        onFeedCardActivated(feedAttribution, discussion.publicId);
    }, [discussion.publicId, feedAttribution]);

    return (
        <SearchResultRowBase
            href={href}
            linkLabel={discussion.title ?? localizer("literal.not_available")}
            typeLabel={localizer("search.tab.discussions")}
            searchAttribution={searchAttribution}
            onActivate={feedAttribution ? handleActivated : undefined}
            contentGuardProps={{
                isSensitive,
                hideSensitiveMedia,
                variant: "card",
                overlayStacking: "below-link-overlay",
                badgeLabel: warningCopy.badgeLabel,
                label: warningCopy.label,
                helperText: warningCopy.helperText,
                revealText: warningCopy.revealText,
            }}
            title={
                <span className="inline-flex flex-wrap items-center gap-2">
                    <span>{highlightMatches(discussion.title ?? localizer("literal.not_available"), highlightTerms)}</span>
                    {isSensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                </span>
            }
            metadata={
                <AuthorAttribution localizer={localizer}
                    name={author}
                    href={authorProfilePath}
                    imageUrl={discussionWithAuthorAvatar.authorProfileImageUrl}
                    avatarSeed={discussion.authorUsername ?? author}
                    details={createdAt ? <time dateTime={discussion.createdAt}>{createdAt}</time> : null}
                    avatarClassName="size-5"
                />
            }
            // Prose only — the slot is clamped. The badges below are a sibling, outside the clamp.
            // An excerpt-less discussion renders no description rather than borrowing the
            // list-empty sentence, which read as "this result is empty" rather than "no excerpt".
            description={highlightMatches(discussion.excerpt?.trim() || undefined, highlightTerms)}
            stats={
                    <CardMetadataBadges
                        className="gap-2"
                        badgeClassName="bg-background2/55"
                        // Each entry is dropped entirely at zero rather than rendered as "0 Downvote".
                        // `CardMetadataBadges.hasContent` returns true for the number 0 — correct in
                        // general, since 0 is legitimately worth showing on some surfaces — so the
                        // decision belongs here, at the call site, exactly as SeriesCardDisplayer and
                        // GroupCardDisplayer already do it. Twenty rows of identical "0 Downvote"
                        // pills were a third of this row's badge line carrying no information.
                        //
                        // The labels are the shared plural patterns rather than `discussion.agreement`
                        // / `.disagreement` / `.thoughts`, which were bare nouns concatenated after a
                        // count ("1 Upvote", "5 thoughts") and disagreed on casing inside one visual
                        // group. These three keys are already translated in all 25 catalogs with the
                        // plural categories each language needs.
                        items={[
                            ...(upvotesLabel ? [{ key: "agreements", label: upvotesLabel }] : []),
                            ...(downvotesLabel ? [{ key: "disagreements", label: downvotesLabel }] : []),
                            ...(thoughtsLabel ? [{ key: "arguments", label: thoughtsLabel }] : []),
                        ]}
                    />
            }
        />
    );
}

export default SearchDiscussionResultRow;
